// Shared vocabulary for the SSH Proxy onboarding wizard.
//
// A session is two separate SSH connections. Operator → gateway: an 8-hour
// SSH certificate the console issued, plus a push approval per session.
// Gateway → target: a key issued for that one session, released once after
// approval, and only for a host and account a policy names. The operator's
// key never touches the second leg. Before a session opens:
//
//   1. the operator holds a current certificate (plain keys are refused),
//   2. a policy names that person, the target host and the account,
//   3. the target is onboarded as an endpoint assigned to this gateway.
//
// Miss any one and the operator sees the same generic refusal — denials are
// deliberately indistinguishable. That is why the wizard's verification steps
// (Step 2 gating, Step 6 checklist) carry more weight than they look.

export const DEFAULT_LISTEN_PORT = 2222;
// TWO DIFFERENT FILES, and the distinction is load-bearing.
//
// sshproxy.env belongs to the INSTALLER: it holds the HMAC key the installer
// generated. Overwriting it destroys it silently — recordings lose their
// integrity sidecar, and the gateway goes on looking perfectly healthy.
//
// console.env is ours. The unit reads both, console.env second so it wins.
export const INSTALLER_ENV_PATH = "/etc/authnull/sshproxy/sshproxy.env";
export const CONSOLE_ENV_PATH = "/etc/authnull/sshproxy/console.env";
export const CONSOLE_ENV_FILE = "console.env";
// Kept as an alias so older references still resolve to the installer's file.
export const ENV_FILE_PATH = INSTALLER_ENV_PATH;
export const SESSION_DIR_ENV = "SSHPROXY_SESSION_DIR";

// The wizard is ONE-TIME SETUP ONLY: register the row, install the gateway,
// then confirm it checks in. Three steps.
//
// Identity, policy, recording and log streaming used to be steps 3-5 here. They
// are not onboarding — they are settings you change for the life of the gateway,
// and burying them in a wizard meant the only route back to them was to re-run
// onboarding. They now live on the per-gateway detail page, where they can be
// edited.
export const STEPS = [
  { key: "jumpHost", index: 1, title: "Register", blurb: "Create the gateway row" },
  { key: "install", index: 2, title: "Install", blurb: "Binary and config" },
  { key: "finish", index: 3, title: "Verify", blurb: "Confirm it checks in" },
];

// ─── Allowed targets ──────────────────────────────────────────────────────────
// SSHPROXY_BACKEND_ALLOWLIST, edited on the gateway's Targets tab. Policy
// already decides which host and account a session may reach; the allowlist is
// an optional extra bound on top. Empty means no local restriction.
//
// Comparison is case-insensitive and literal: a bare host matches any port;
// host:port must match exactly. No CIDR, no tag selectors — `10.0.0.5` and
// `web-01.prod` are two different entries for one machine.

// host or host:port. Rejects CIDR (a slash), spaces, and empty hosts. A port,
// when present, must be 1..65535.
export function isValidTarget(raw) {
  const v = String(raw || "").trim();
  if (!v || /\s/.test(v) || v.includes("/")) return false;
  const parts = v.split(":");
  if (parts.length > 2) return false;
  const [host, port] = parts;
  if (!host) return false;
  if (parts.length === 2) {
    if (!/^\d+$/.test(port)) return false;
    const n = Number(port);
    if (n < 1 || n > 65535) return false;
  }
  return true;
}

export function normalizeTarget(raw) {
  return String(raw || "").trim().toLowerCase();
}

// Splits "account@host" for the Step 6 target picker — the proxy routes on the
// compound username, splitting on the FIRST `@` into account and host.
export function splitTargetAccount(compound) {
  const v = String(compound || "");
  const at = v.indexOf("@");
  if (at < 0) return { account: "", host: v };
  return { account: v.slice(0, at), host: v.slice(at + 1) };
}

// ─── Operator public keys ─────────────────────────────────────────────────────
// authorized_keys form: `ssh-ed25519 AAAA… comment`. A pasted key with a line
// break in it is the usual failure, so validate before submitting. Never send a
// fingerprint — the server computes it and ignores any the caller supplies.
const KEY_TYPES = [
  "ssh-ed25519",
  "ssh-rsa",
  "ecdsa-sha2-nistp256",
  "ecdsa-sha2-nistp384",
  "ecdsa-sha2-nistp521",
  "sk-ssh-ed25519@openssh.com",
  "sk-ecdsa-sha2-nistp256@openssh.com",
];

export function isValidPublicKey(raw) {
  const v = String(raw || "").trim();
  if (!v || /[\r\n]/.test(v)) return false; // a line break is the usual paste failure
  const parts = v.split(/\s+/);
  if (parts.length < 2) return false;
  const [type, blob] = parts;
  if (!KEY_TYPES.includes(type)) return false;
  // base64 body, ~40+ chars for even the smallest key
  return /^[A-Za-z0-9+/=]+$/.test(blob) && blob.length >= 40;
}

// ─── Step 6 connect command ────────────────────────────────────────────────────
// The one string the operator copies, so it has to be exact. The proxy has no
// SSH channel to carry a target, so the username carries it:
//   ssh <targetAccount>@<targetHost>@<proxyAddress> -p <listenPort>
// Never emit `-J`, and never emit a bare `ssh user@proxy`.
export function buildConnectCommand({ account, host, proxyAddress, listenPort }) {
  const acct = account || "<account>";
  const tgt = host || "<host>";
  const proxy = proxyAddress || "<proxy>";
  const port = listenPort || DEFAULT_LISTEN_PORT;
  return `ssh ${acct}@${tgt}@${proxy} -p ${port}`;
}

// ─── Step 4 policy ─────────────────────────────────────────────────────────────
// A registered key says who someone is; the policy says what they may reach. No
// policy at all means deny on SSH (#338), so this step is what grants
// access. EMPTY MEANS ANY, consistently: leaving `hosts` blank grants every
// host, the exact opposite of Step 1's allowlist (blank there refuses every
// host). Say it on screen. A policy also only takes effect once Approved.
export function policyGrantSummary({ users, groups, matchAll, hosts, accounts, jumpServers, proxyName }) {
  const who = matchAll
    ? "Anyone in the org"
    : [
        ...(users?.length ? [users.length === 1 ? users[0] : `${users.length} users`] : []),
        ...(groups?.length ? [groups.length === 1 ? `group ${groups[0]}` : `${groups.length} groups`] : []),
      ].join(" and ") || "No one yet (add users, groups, or match-all)";

  const account = accounts?.length ? (accounts.length === 1 ? `the ${accounts[0]} account` : `${accounts.length} accounts`) : "any account";
  const host = hosts?.length ? (hosts.length === 1 ? hosts[0] : `${hosts.length} hosts`) : "any host";
  const via = jumpServers?.length ? (proxyName || "this proxy") : "any jump server";

  return `${who} may reach ${account} on ${host} through ${via}, with an MFA push.`;
}

// ─── Liveness (jump-server list, right rail, Step 2 gating) ───────────────────
// `status` is the administrative state an admin set; liveness is observed from
// the heartbeat. They are different facts and must not share a column — a proxy
// can be enabled and not running, or running and disabled.
//
// Three states, not two. A proxy that has NEVER heartbeated is not "offline":
// the usual cause is SSHPROXY_CONTROL_URL being unset, which is a perfectly
// working state for SSH itself — sessions open, the console just never hears
// about the process. Saying "offline" there sends admins to restart a service
// that is already running.
export const HEARTBEAT_INTERVAL_SECONDS = 60;
export const STALE_AFTER_INTERVALS = 3; // ~180s

export function formatRelativeAge(seconds) {
  const s = Math.max(0, Math.round(seconds));
  if (s < 90) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 90) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

// → { state: "never" | "live" | "stale", label, tone, ageSeconds }
export function proxyLiveness(lastSeenAt, intervalSeconds = HEARTBEAT_INTERVAL_SECONDS) {
  if (!lastSeenAt) return { state: "never", label: "Never seen", tone: "idle", ageSeconds: null };
  const seen = new Date(lastSeenAt).getTime();
  if (Number.isNaN(seen)) return { state: "never", label: "Never seen", tone: "idle", ageSeconds: null };
  const ageSeconds = Math.max(0, (Date.now() - seen) / 1000);
  if (ageSeconds > intervalSeconds * STALE_AFTER_INTERVALS) {
    return { state: "stale", label: `Last seen ${formatRelativeAge(ageSeconds)}`, tone: "warn", ageSeconds };
  }
  return { state: "live", label: "Live", tone: "ok", ageSeconds };
}

// Wizard wording for the same three states — the right rail and Step 2 talk
// about installing, the grid talks about seeing.
export function livenessState(lastSeenAt, intervalSeconds = HEARTBEAT_INTERVAL_SECONDS) {
  const l = proxyLiveness(lastSeenAt, intervalSeconds);
  if (l.state === "never") return { ...l, label: "Never seen" };
  if (l.state === "live") return { ...l, label: "Running" };
  return l;
}

// ─── Step 2 · the env file ────────────────────────────────────────────────────
// Everything the proxy needs to reach back to Authnull, composed into the file
// it reads at /etc/authnull/sshproxy/sshproxy.env.
//
// SSHPROXY_INTERNAL_API_KEY is deliberately left EMPTY. It comes from the
// stack's own .env and the console never sees it — so the download is a
// head start, not a finished file, and the comment beside it has to say so
// rather than letting someone copy it over and wonder why nothing starts.
export const INTERNAL_API_KEY_VAR = "SSHPROXY_INTERNAL_API_KEY";

export function buildProxyEnvFile({ orgId, tenantId, jumpServerId, authnUrl, controlUrl, recordingUrl }) {
  const line = (k, v) => `${k}=${v ?? ""}`;
  return [
    "# Authnull SSH proxy — console configuration",
    `# Gateway ${jumpServerId ?? "<unknown>"} · ${new Date().toISOString()}`,
    "#",
    `# Install at ${CONSOLE_ENV_PATH}`,
    `# Do NOT copy this over ${INSTALLER_ENV_PATH} — that file belongs to the`,
    "# installer and holds the HMAC key it generated.",
    "",
    line("SSHPROXY_ORG_ID", orgId),
    line("SSHPROXY_TENANT_ID", tenantId),
    line("SSHPROXY_JUMP_SERVER_ID", jumpServerId),
    "",
    line("SSHPROXY_AUTHN_URL", authnUrl),
    line("SSHPROXY_CONTROL_URL", controlUrl),
    line("SSHPROXY_RECORDING_URL", recordingUrl),
    "",
    "# Copy this from the stack's own .env — the console cannot read it, and",
    "# the gateway will not start while it is blank.",
    `${INTERNAL_API_KEY_VAR}=`,
    "",
  ].join("\n");
}

// Client-side file download, matching the pattern GatewayInstall.jsx uses for
// sensor.yml. No endpoint involved.
export function downloadTextFile(filename, contents, mime = "text/plain") {
  const blob = new Blob([contents], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// ─── Legacy · CA trust ────────────────────────────────────────────────────────
// The gateway CA, TrustedUserCAKeys and SSHPROXY_CERT_HOSTS are gone: the
// gateway now reaches each target with a per-session grant key. Nothing in the
// gateway pages uses these any more; they stay exported only until the
// endpoint screens that still import them are rewritten.
export const CA_FILE_PATH = "/etc/ssh/authnull_ca.pub";
export const CA_DROPIN_PATH = "/etc/ssh/sshd_config.d/60-authnull-ca.conf";
// Optional extra bound on which targets the gateway may reach. Empty means no
// local restriction — policy decides. Still used by the Targets tab.
export const BACKEND_ALLOWLIST_ENV = "SSHPROXY_BACKEND_ALLOWLIST";
// Legacy — per-host certificate settings no longer exist on the gateway.
export const CERT_HOSTS_ENV = "SSHPROXY_CERT_HOSTS";

export function buildCaTrustCommands(caPublicKey) {
  const key = caPublicKey || "<caPublicKey — waiting for the gateway to report>";
  return `sudo tee ${CA_FILE_PATH} >/dev/null <<'EOF'
${key}
EOF
sudo chmod 644 ${CA_FILE_PATH}

echo 'TrustedUserCAKeys ${CA_FILE_PATH}' |
  sudo tee ${CA_DROPIN_PATH}

sudo sshd -t && sudo systemctl reload ssh`;
}

// ─── Formatting shared by the recordings list and the player ──────────────────
export function formatBytes(n) {
  const b = Number(n);
  if (!Number.isFinite(b) || b <= 0) return null;
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = b;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

// Every field BE-1 added is `omitempty`, so a Guacamole row — which predates
// all of them — omits rather than blanks. A missing fact reads as an em dash,
// never as empty space that looks like a rendering bug.
export const EMPTY = "—";
export function orDash(v) {
  if (v === null || v === undefined) return EMPTY;
  const s = String(v).trim();
  return s === "" ? EMPTY : s;
}
